import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import hotelService from '../services/hotelService';
import accountService from '../services/accountService';
import { useAccount, usePartnerAccess } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import { saveListNav } from '../utils/listNav';
import './VaultSourceList.css';
import './BillingTab.css';   // .billing-doc-badge-* — the same stage badges as the hotel's Billing tab

// CONTRACT AND INVOICES per hotel (the list lambda's billingStatus, V666 / V669): where its latest billing document is,
// and whether the invoices raised from it are paid — balances refreshed hourly from QuickBooks.
const CONTRACT_STAGES = {
  GENERATED: { label: 'To sign', className: 'billing-doc-badge-amber' },
  HOTEL_SIGNED: { label: 'Signed by hotel', className: 'billing-doc-badge-blue' },
  COUNTERSIGNED: { label: 'Countersigned', className: 'billing-doc-badge-green' },
};
const money = (amount, currency) => {
  try { return new Intl.NumberFormat('en-IE', { style: 'currency', currency: currency || 'EUR' }).format(amount); }
  catch (e) { return `${currency || ''} ${Number(amount).toFixed(2)}`; }
};
const ContractCell = ({ status }) => {
  const c = status && status.contract;
  if (!c) return <span className="billing-muted">—</span>;
  const stage = CONTRACT_STAGES[c.status] || CONTRACT_STAGES.GENERATED;
  return <span className={`billing-doc-badge ${stage.className}`} title={`${c.templateName} · billing period from ${c.periodFrom}`}>{stage.label}</span>;
};
const InvoicesCell = ({ status }) => {
  const i = status && status.invoices;
  if (!i || !i.total) return <span className="billing-muted">—</span>;
  if (!i.owing) return <span className="billing-doc-badge billing-doc-badge-green">{i.total === 1 ? 'Paid' : `All ${i.total} paid`}</span>;
  return (
    <span className={`billing-doc-badge ${i.overdue ? 'billing-doc-badge-red' : 'billing-doc-badge-amber'}`}
      title={`${i.total} invoiced · ${i.paid} paid · ${i.owing} owing`}>
      {i.overdue ? `${i.overdue} overdue` : `${i.owing} owing`} · {money(i.due, i.currency)}
    </span>
  );
};

const HotelList = () => {
  const navigate = useNavigate();
  const { selectedAccount, selectedPartner } = useAccount();
  const { isSuperAdmin } = useAuth();
  const { stripeReady } = usePartnerAccess(isSuperAdmin);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadHotels();
  }, [selectedAccount, selectedPartner]);

  const handleDelete = async (e, hotel) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${hotel.hotelname || hotel.HOTELNAME}"? This cannot be undone.`)) return;
    try {
      await hotelService.deleteHotel(hotel.hotelid, hotel.accountid);
      setHotels(prev => prev.filter(h => h.hotelid !== hotel.hotelid));
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to delete hotel');
    }
  };

  const loadHotels = async () => {
    if (!selectedAccount && !selectedPartner) {
      setHotels([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (selectedAccount) {
        const response = await hotelService.getAllHotels({ accountid: selectedAccount });
        setHotels(response.hotels || []);
      } else {
        // Partner selected — aggregate hotels across all partner accounts
        const accountsResponse = await accountService.getAllAccounts({ partnerid: selectedPartner });
        const partnerAccounts = accountsResponse.accounts || [];
        const hotelResponses = await Promise.all(
          partnerAccounts.map(a => {
            const accountId = a.ACCOUNTID ?? a.id;
            if (!accountId) return Promise.resolve({ hotels: [] });
            return hotelService.getAllHotels({ accountid: accountId }).catch(() => ({ hotels: [] }));
          })
        );
        setHotels(hotelResponses.flatMap(r => r.hotels || []));
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load hotels');
    } finally {
      setLoading(false);
    }
  };

  const filteredHotels = hotels.filter(h => {
    const name = (h.hotelname || h.HOTELNAME || '').toLowerCase();
    const code = (h.hotelcode || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || code.includes(term);
  });

  // Opening a hotel remembers this list's order (as filtered), so the hotel page can offer Previous / Next.
  const openHotel = (hotel, suffix) => {
    saveListNav('hotels', filteredHotels.map(h => ({ id: h.hotelid, label: h.hotelname || h.HOTELNAME || h.hotelcode || '' })));
    navigate(`/hotel/${hotel.hotelid}${suffix}?returnTo=/hotels`);
  };

  if (loading) return <div className="loading">Loading hotels...</div>;

  if (!selectedAccount && !selectedPartner) {
    return (
      <div className="vaultsource-list-container">
        <div className="header"><h1>Hotels</h1></div>
        <div className="no-data" style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
          Select a partner or hotel chain to view hotels.
        </div>
      </div>
    );
  }

  return (
    <div className="vaultsource-list-container">
      <div className="header">
        <h1>Hotels</h1>
        {stripeReady ? (
          <button
            className="btn btn-primary"
            onClick={() => navigate(selectedAccount ? `/accounts/${selectedAccount}/hotel/new?returnTo=/hotels` : '/hotel/new?returnTo=/hotels')}
          >
            ➕ Add Hotel
          </button>
        ) : (
          <span title="Connect Stripe to add hotels" style={{ fontSize: '0.8rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '0.4rem 0.75rem' }}>
            Stripe required to add hotels
          </span>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button className="btn btn-secondary" onClick={() => setSearchTerm('')}>Clear</button>
        )}
      </div>

      <div className="table-container">
        <table className="vaultsource-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Name</th>
              <th>Code</th>
              <th>Country</th>
              <th>Contact</th>
              <th>Contract</th>
              <th>Invoices</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredHotels.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  {hotels.length === 0 ? 'No hotels found' : 'No hotels match your search'}
                </td>
              </tr>
            ) : (
              filteredHotels.map((hotel) => (
                <tr
                  key={hotel.hotelid}
                  onClick={() => openHotel(hotel, '')}
                  className="clickable-row"
                  title="Click to view"
                >
                  <td>{hotel.chain_hotel_number || '—'}</td>
                  <td>{hotel.hotelname || hotel.HOTELNAME || '—'}</td>
                  <td>{hotel.hotelcode || '—'}</td>
                  <td>{hotel.COUNTRY || '—'}</td>
                  <td>{hotel.CONTACTNAME || '—'}</td>
                  <td><ContractCell status={hotel.billingStatus} /></td>
                  <td><InvoicesCell status={hotel.billingStatus} /></td>
                  <td className="actions">
                    <button
                      className="btn btn-edit"
                      onClick={(e) => { e.stopPropagation(); openHotel(hotel, '/edit'); }}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn btn-delete"
                      onClick={(e) => handleDelete(e, hotel)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HotelList;
